/**
 * WhatsApp Manager inside LY360: message templates (build, submit for approval, track, edit, delete, sync) and the
 * health of each number and business account (display name review, quality rating, messaging limit, restrictions),
 * through Meta's WhatsApp Business Management API with the customer's own connection. No intermediary.
 *
 * VERIFY against the current WhatsApp Business Management API reference before production: field names (quality,
 * messaging limits moved between phone number and business portfolio over time), template component rules, edit
 * limits, and the account-update webhook payloads. The local sandbox (provider-mocks) implements these shapes.
 */

// --------------------------------------------------------------------------------------------- templates

export const TEMPLATE_CATEGORIES = ['marketing', 'utility', 'authentication'] as const;
export type TemplateCategory = (typeof TEMPLATE_CATEGORIES)[number];
export const TEMPLATE_LANGUAGES = [
  'en',
  'en_US',
  'en_GB',
  'ar',
  'hi',
  'ml',
  'ta',
  'ur',
  'fil',
  'fr',
] as const;

export type TemplateButton =
  | { type: 'quick_reply'; text: string }
  | { type: 'url'; text: string; url: string }
  | { type: 'phone'; text: string; phone: string };

export interface TemplateContent {
  header?:
    { format: 'text'; text: string } | { format: 'image' | 'video' | 'document'; mediaUrl: string } | null;
  /** With {{1}}, {{2}}… for the parts filled in when sending. */
  body: string;
  /** An example for each variable, in order (Meta reviews templates with examples). */
  examples: string[];
  footer?: string | null;
  buttons?: TemplateButton[];
}

export interface TemplateInput extends TemplateContent {
  name: string;
  language: string;
  category: TemplateCategory;
}

export interface TemplateIssue {
  path: string;
  code: string;
  max?: number;
}

const VAR = /\{\{(\d+)\}\}/g;
export const templateVariables = (text: string) => [...text.matchAll(VAR)].map((m) => Number(m[1]));

/** The rules Meta enforces, checked before submitting so a template is not rejected for form. */
export function checkTemplate(t: TemplateInput): TemplateIssue[] {
  const out: TemplateIssue[] = [];
  if (!/^[a-z0-9_]{1,512}$/.test(t.name)) out.push({ path: 'name', code: 'name_format' });
  if (!t.body.trim()) out.push({ path: 'body', code: 'required' });
  if (t.body.length > 1024) out.push({ path: 'body', code: 'too_long', max: 1024 });
  const vars = templateVariables(t.body);
  const distinct = [...new Set(vars)].sort((a, b) => a - b);
  if (distinct.some((v, i) => v !== i + 1)) out.push({ path: 'body', code: 'variables_sequence' });
  if (t.examples.length < distinct.length || t.examples.slice(0, distinct.length).some((e) => !e.trim()))
    out.push({ path: 'examples', code: 'examples_required' });
  const trimmed = t.body.trim();
  if (/^\{\{\d+\}\}/.test(trimmed) || /\{\{\d+\}\}$/.test(trimmed))
    out.push({ path: 'body', code: 'variable_at_edge' });
  if (/\{\{\d+\}\}\s*\{\{\d+\}\}/.test(t.body)) out.push({ path: 'body', code: 'variables_adjacent' });
  if (t.header?.format === 'text') {
    if (t.header.text.length > 60) out.push({ path: 'header', code: 'too_long', max: 60 });
    if (templateVariables(t.header.text).length) out.push({ path: 'header', code: 'no_variables' });
  }
  // Meta fetches the sample from a public address (https in production).
  if (t.header && t.header.format !== 'text' && !/^https?:\/\/\S+$/.test(t.header.mediaUrl))
    out.push({ path: 'header', code: 'media_url' });
  if (t.footer && t.footer.length > 60) out.push({ path: 'footer', code: 'too_long', max: 60 });
  if (t.footer && templateVariables(t.footer).length) out.push({ path: 'footer', code: 'no_variables' });
  const b = t.buttons ?? [];
  if (b.length > 10) out.push({ path: 'buttons', code: 'too_many_buttons', max: 10 });
  if (b.filter((x) => x.type === 'url').length > 2)
    out.push({ path: 'buttons', code: 'too_many_url', max: 2 });
  if (b.filter((x) => x.type === 'phone').length > 1)
    out.push({ path: 'buttons', code: 'too_many_phone', max: 1 });
  for (const [i, x] of b.entries()) {
    if (!x.text.trim() || x.text.length > 25)
      out.push({ path: `buttons.${i}`, code: 'button_text', max: 25 });
    if (x.type === 'url' && !/^https:\/\/\S+$/.test(x.url)) out.push({ path: `buttons.${i}`, code: 'url' });
    if (x.type === 'phone' && !/^\+\d{7,15}$/.test(x.phone))
      out.push({ path: `buttons.${i}`, code: 'phone' });
  }
  if (t.category === 'authentication') out.push({ path: 'category', code: 'authentication_not_supported' });
  return out;
}

type Json = Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any

/** Our template content as Meta's `components`. */
export function toMetaComponents(t: TemplateContent): Json[] {
  const c: Json[] = [];
  if (t.header?.format === 'text') c.push({ type: 'HEADER', format: 'TEXT', text: t.header.text });
  else if (t.header)
    c.push({
      type: 'HEADER',
      format: t.header.format.toUpperCase(),
      example: { header_handle: [t.header.mediaUrl] },
    });
  const vars = [...new Set(templateVariables(t.body))];
  c.push({
    type: 'BODY',
    text: t.body,
    ...(vars.length ? { example: { body_text: [t.examples.slice(0, vars.length)] } } : {}),
  });
  if (t.footer) c.push({ type: 'FOOTER', text: t.footer });
  if (t.buttons?.length)
    c.push({
      type: 'BUTTONS',
      buttons: t.buttons.map((b) =>
        b.type === 'quick_reply'
          ? { type: 'QUICK_REPLY', text: b.text }
          : b.type === 'url'
            ? { type: 'URL', text: b.text, url: b.url }
            : { type: 'PHONE_NUMBER', text: b.text, phone_number: b.phone },
      ),
    });
  return c;
}

/** Meta's `components` back into our template content (templates made in WhatsApp Manager are imported too). */
export function fromMetaComponents(components: Json[] = []): TemplateContent {
  const header = components.find((x) => x.type === 'HEADER');
  const body = components.find((x) => x.type === 'BODY');
  const footer = components.find((x) => x.type === 'FOOTER');
  const buttons = components.find((x) => x.type === 'BUTTONS')?.buttons as Json[] | undefined;
  return {
    header: !header
      ? null
      : header.format === 'TEXT'
        ? { format: 'text', text: String(header.text ?? '') }
        : {
            format: String(header.format).toLowerCase() as 'image' | 'video' | 'document',
            mediaUrl: String(header.example?.header_handle?.[0] ?? ''),
          },
    body: String(body?.text ?? ''),
    examples: ((body?.example?.body_text?.[0] ?? []) as unknown[]).map(String),
    footer: footer?.text ?? null,
    buttons: (buttons ?? []).flatMap<TemplateButton>((b) =>
      b.type === 'QUICK_REPLY'
        ? [{ type: 'quick_reply', text: String(b.text) }]
        : b.type === 'URL'
          ? [{ type: 'url', text: String(b.text), url: String(b.url) }]
          : b.type === 'PHONE_NUMBER'
            ? [{ type: 'phone', text: String(b.text), phone: String(b.phone_number) }]
            : [],
    ),
  };
}

export type TemplateStatus =
  'draft' | 'pending' | 'approved' | 'rejected' | 'paused' | 'disabled' | 'in_appeal';
const STATUS: Record<string, TemplateStatus> = {
  APPROVED: 'approved',
  PENDING: 'pending',
  IN_APPEAL: 'in_appeal',
  REJECTED: 'rejected',
  PAUSED: 'paused',
  DISABLED: 'disabled',
  PENDING_DELETION: 'disabled',
  DELETED: 'disabled',
  LIMIT_EXCEEDED: 'rejected',
};
export const templateStatusOf = (s: unknown): TemplateStatus => STATUS[String(s).toUpperCase()] ?? 'pending';

export interface MetaTemplate {
  externalId: string;
  name: string;
  language: string;
  category: TemplateCategory;
  status: TemplateStatus;
  rejectedReason: string | null;
  quality: 'green' | 'yellow' | 'red' | 'unknown';
  content: TemplateContent;
}

// ---------------------------------------------------------------------------------------- number health

export interface NumberHealth {
  displayPhoneNumber: string | null;
  verifiedName: string | null;
  /** Display name review: approved, pending review, declined, none. */
  nameStatus: string | null;
  quality: 'green' | 'yellow' | 'red' | 'unknown';
  /** How many different customers it can start conversations with a day. */
  messagingLimit: string | null;
  /** connected, flagged, restricted, banned, disconnected… */
  status: string | null;
  mode: 'live' | 'sandbox' | null;
  throughput: string | null;
  codeVerification: string | null;
}

export interface BusinessHealth {
  name: string | null;
  reviewStatus: string | null;
  businessVerification: string | null;
  currency: string | null;
}

const lower = (v: unknown) => (v == null ? null : String(v).toLowerCase());
const qualityOf = (v: unknown): NumberHealth['quality'] =>
  (['green', 'yellow', 'red'] as const).find((q) => q === lower(v)) ?? 'unknown';
/** "TIER_1K" → "1k", "TIER_UNLIMITED" → "unlimited". */
const limitOf = (v: unknown) =>
  v == null
    ? null
    : String(v)
        .replace(/^TIER_/i, '')
        .toLowerCase();

// ---------------------------------------------------------------------------------------------- the API

export class WhatsAppApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: number | null = null,
  ) {
    super(message);
    this.name = 'WhatsAppApiError';
  }
}

async function graph<T>(
  base: string,
  token: string,
  path: string,
  init: RequestInit & { json?: unknown } = {},
): Promise<T> {
  const { json, ...rest } = init;
  const res = await fetch(`${base}${path}`, {
    ...rest,
    headers: {
      authorization: `Bearer ${token}`,
      accept: 'application/json',
      ...(json !== undefined ? { 'content-type': 'application/json' } : {}),
    },
    ...(json !== undefined ? { body: JSON.stringify(json) } : {}),
    signal: AbortSignal.timeout(15_000),
  }).catch((e: Error) => {
    throw new WhatsAppApiError(`could not reach Meta: ${e.message}`, 0);
  });
  const body = (await res.json().catch(() => ({}))) as T & {
    error?: { message?: string; error_user_msg?: string; code?: number };
  };
  if (!res.ok) {
    const e = body.error;
    throw new WhatsAppApiError(
      String(e?.error_user_msg ?? e?.message ?? `Meta answered ${res.status}`).slice(0, 300),
      res.status,
      e?.code ?? null,
    );
  }
  return body;
}

export function whatsappManager(graphBase: string, token: string) {
  return {
    async templates(wabaId: string): Promise<MetaTemplate[]> {
      const out: MetaTemplate[] = [];
      let path: string | null =
        `/${wabaId}/message_templates?fields=id,name,language,status,category,components,quality_score,rejected_reason&limit=100`;
      for (let page = 0; path && page < 20; page++) {
        const r: { data?: Json[]; paging?: { next?: string } } = await graph(graphBase, token, path);
        for (const t of r.data ?? [])
          out.push({
            externalId: String(t.id),
            name: String(t.name),
            language: String(t.language),
            category: (lower(t.category) as TemplateCategory) ?? 'utility',
            status: templateStatusOf(t.status),
            rejectedReason:
              t.rejected_reason && t.rejected_reason !== 'NONE' ? String(t.rejected_reason) : null,
            quality: qualityOf(t.quality_score?.score),
            content: fromMetaComponents(t.components),
          });
        // Following pages come as absolute URLs; keep them relative to our base (the sandbox in development).
        path = r.paging?.next ? r.paging.next.replace(/^https?:\/\/[^/]+\/v[\d.]+/, '') : null;
      }
      return out;
    },
    create: (wabaId: string, t: TemplateInput) =>
      graph<{ id: string; status: string; category: string }>(
        graphBase,
        token,
        `/${wabaId}/message_templates`,
        {
          method: 'POST',
          json: {
            name: t.name,
            language: t.language,
            category: t.category.toUpperCase(),
            components: toMetaComponents(t),
          },
        },
      ),
    edit: (templateId: string, t: TemplateContent & { category?: TemplateCategory }) =>
      graph<{ success: boolean }>(graphBase, token, `/${templateId}`, {
        method: 'POST',
        json: {
          components: toMetaComponents(t),
          ...(t.category ? { category: t.category.toUpperCase() } : {}),
        },
      }),
    remove: (wabaId: string, name: string, templateId?: string | null) =>
      graph<{ success: boolean }>(
        graphBase,
        token,
        `/${wabaId}/message_templates?name=${encodeURIComponent(name)}${templateId ? `&hsm_id=${encodeURIComponent(templateId)}` : ''}`,
        { method: 'DELETE' },
      ),
    async number(phoneNumberId: string): Promise<NumberHealth> {
      const n = await graph<Json>(
        graphBase,
        token,
        `/${phoneNumberId}?fields=display_phone_number,verified_name,name_status,quality_rating,messaging_limit_tier,status,account_mode,throughput,code_verification_status`,
      );
      return {
        displayPhoneNumber: n.display_phone_number ?? null,
        verifiedName: n.verified_name ?? null,
        nameStatus: lower(n.name_status),
        quality: qualityOf(n.quality_rating),
        messagingLimit: limitOf(n.messaging_limit_tier),
        status: lower(n.status),
        mode: lower(n.account_mode) === 'sandbox' ? 'sandbox' : n.account_mode ? 'live' : null,
        throughput: lower(n.throughput?.level),
        codeVerification: lower(n.code_verification_status),
      };
    },
    async business(wabaId: string): Promise<BusinessHealth> {
      const w = await graph<Json>(
        graphBase,
        token,
        `/${wabaId}?fields=name,account_review_status,business_verification_status,currency`,
      );
      return {
        name: w.name ?? null,
        reviewStatus: lower(w.account_review_status),
        businessVerification: lower(w.business_verification_status),
        currency: w.currency ?? null,
      };
    },
    /** A newly added number starts sending once registered with its two-step PIN. */
    register: (phoneNumberId: string, pin: string) =>
      graph<{ success: boolean }>(graphBase, token, `/${phoneNumberId}/register`, {
        method: 'POST',
        json: { messaging_product: 'whatsapp', pin },
      }),
  };
}

// ------------------------------------------------------------------------------- account notifications

export type AccountEvent =
  | {
      kind: 'template_status';
      wabaId: string;
      templateId: string;
      name: string;
      language: string;
      status: TemplateStatus;
      reason: string | null;
    }
  | {
      kind: 'template_quality';
      wabaId: string;
      templateId: string;
      name: string;
      language: string;
      quality: 'green' | 'yellow' | 'red' | 'unknown';
    }
  | {
      kind: 'template_category';
      wabaId: string;
      templateId: string;
      name: string;
      language: string;
      category: string;
    }
  | { kind: 'quality'; wabaId: string; phone: string; event: string; messagingLimit: string | null }
  | { kind: 'name'; wabaId: string; phone: string; decision: string; requestedName: string | null }
  | { kind: 'account'; wabaId: string; event: string; detail: string | null };

const ACCOUNT_FIELDS = new Set([
  'message_template_status_update',
  'message_template_quality_update',
  'template_category_update',
  'phone_number_quality_update',
  'phone_number_name_update',
  'account_update',
  'account_review_update',
]);

/** True when a WhatsApp webhook carries account notifications rather than messages. */
export const hasAccountEvents = (body: Record<string, unknown>) =>
  Array.isArray(body.entry) &&
  (body.entry as Json[]).some((e) =>
    (e.changes as Json[] | undefined)?.some((c) => ACCOUNT_FIELDS.has(String(c.field))),
  );

/** Template reviews, quality changes, display name decisions and restrictions, from a WhatsApp webhook. */
export function parseAccountEvents(body: Record<string, unknown>): AccountEvent[] {
  const out: AccountEvent[] = [];
  for (const e of (body.entry as Json[] | undefined) ?? [])
    for (const c of (e.changes as Json[] | undefined) ?? []) {
      const v = (c.value ?? {}) as Json;
      const wabaId = String(e.id);
      const tpl = {
        wabaId,
        templateId: String(v.message_template_id ?? ''),
        name: String(v.message_template_name ?? ''),
        language: String(v.message_template_language ?? ''),
      };
      switch (c.field) {
        case 'message_template_status_update':
          out.push({
            kind: 'template_status',
            ...tpl,
            status: templateStatusOf(v.event),
            reason: v.reason && v.reason !== 'NONE' ? String(v.reason) : null,
          });
          break;
        case 'message_template_quality_update':
          out.push({ kind: 'template_quality', ...tpl, quality: qualityOf(v.new_quality_score) });
          break;
        case 'template_category_update':
          out.push({
            kind: 'template_category',
            ...tpl,
            category: String(v.new_category ?? '').toLowerCase(),
          });
          break;
        case 'phone_number_quality_update':
          out.push({
            kind: 'quality',
            wabaId,
            phone: String(v.display_phone_number ?? ''),
            event: lower(v.event) ?? 'unknown',
            messagingLimit: limitOf(v.current_limit),
          });
          break;
        case 'phone_number_name_update':
          out.push({
            kind: 'name',
            wabaId,
            phone: String(v.display_phone_number ?? ''),
            decision: lower(v.decision) ?? 'unknown',
            requestedName: v.requested_verified_name ?? null,
          });
          break;
        case 'account_update':
        case 'account_review_update':
          out.push({
            kind: 'account',
            wabaId,
            event: lower(v.event ?? v.decision) ?? 'unknown',
            detail:
              v.ban_info?.waba_ban_state ??
              v.restriction_info?.[0]?.restriction_type ??
              v.violation_info?.violation_type ??
              null,
          });
          break;
      }
    }
  return out;
}

// ------------------------------------------------------------------------------------- suggested templates

/** Ready-to-submit templates for the automations and common campaigns, in English and Arabic. */
export const TEMPLATE_LIBRARY: (TemplateInput & { use: string })[] = [
  ...pair('order_confirmation', 'utility', 'order', ['Sara', 'ORD-1042', 'QAR 578.00'], {
    en: 'Hi {{1}}, thank you for your order {{2}} ({{3}}). We will message you when it is on its way.',
    ar: 'أهلًا {{1}}، شكرًا لطلبك {{2}} ({{3}}). سنراسلك عند شحنه.',
  }),
  ...pair('order_shipped', 'utility', 'order', ['Sara', 'ORD-1042', 'https://track.example/QP123'], {
    en: 'Good news {{1}}: your order {{2}} is on its way. Track it here: {{3}} Thank you for shopping with us.',
    ar: 'خبر سار {{1}}: طلبك {{2}} في الطريق. تتبّعه من هنا: {{3}} شكرًا لتسوقك معنا.',
  }),
  ...pair('review_request', 'marketing', 'order', ['Sara', 'ORD-1042'], {
    en: 'Hi {{1}}, how are you enjoying your order {{2}}? Reply with a quick review, we read every one.',
    ar: 'أهلًا {{1}}، ما رأيك في طلبك {{2}}؟ رد بتقييم سريع، نقرأ كل رد.',
  }),
  ...pair('cart_reminder', 'marketing', 'cart', ['Sara', 'https://shop.example/cart/abc'], {
    en: 'Hi {{1}}, you left something in your basket. It is still waiting for you here: {{2}} Reply if you have any questions.',
    ar: 'أهلًا {{1}}، تركت شيئًا في سلتك وما زال بانتظارك هنا: {{2}} رد علينا إن كان لديك أي سؤال.',
  }),
  ...pair(
    'cart_reminder_offer',
    'marketing',
    'cart',
    ['Sara', 'https://shop.example/cart/abc', 'COMEBACK10'],
    {
      en: 'Still thinking, {{1}}? Complete your order here: {{2}} and use code {{3}} for a little extra off.',
      ar: 'ما زلت تفكر يا {{1}}؟ أكمل طلبك من هنا: {{2}} واستخدم الرمز {{3}} لخصم إضافي.',
    },
  ),
  ...pair('back_in_stock', 'marketing', 'stock', ['Sara'], {
    en: 'Good news {{1}}: the product you asked about is back in stock. Reply to reserve yours. Reply STOP to stop offers.',
    ar: 'خبر سار {{1}}: المنتج الذي سألت عنه عاد إلى المخزون. رد على هذه الرسالة لحجزه. أرسل "إيقاف" لإيقاف العروض.',
  }),
  ...pair('refill_reminder', 'marketing', 'loyalty', ['Sara', 'Versace Eros 100 ml'], {
    en: 'Hi {{1}}, your {{2}} should be nearly finished. Reply here and we will deliver a new one.',
    ar: 'أهلًا {{1}}، يُتوقع أن {{2}} قارب على الانتهاء. رد هنا ونوصل لك واحدًا جديدًا.',
  }),
  ...pair('birthday_offer', 'marketing', 'loyalty', ['Sara', 'BDAY15'], {
    en: 'Happy birthday {{1}}! A gift from us: code {{2}} for 15% off this week.',
    ar: 'كل عام وأنت بخير يا {{1}}! هدية منا: الرمز {{2}} لخصم ١٥٪ هذا الأسبوع.',
  }),
  ...pair('win_back', 'marketing', 'loyalty', ['Sara', 'MISSYOU20'], {
    en: 'We miss you {{1}}! Here is {{2}} for 20% off your next order.',
    ar: 'اشتقنا لك يا {{1}}! هذا الرمز {{2}} لخصم ٢٠٪ على طلبك القادم.',
  }),
  ...pair('appointment_reminder', 'utility', 'service', ['Sara', 'Tuesday 4 pm'], {
    en: 'Hi {{1}}, a reminder of your appointment on {{2}}. Reply to change it.',
    ar: 'أهلًا {{1}}، نذكّرك بموعدك يوم {{2}}. رد لتغييره.',
  }),
];

function pair(
  name: string,
  category: TemplateCategory,
  use: string,
  examples: string[],
  body: { en: string; ar: string },
): (TemplateInput & { use: string })[] {
  return [
    { name, language: 'en', category, body: body.en, examples, use, footer: null, buttons: [], header: null },
    { name, language: 'ar', category, body: body.ar, examples, use, footer: null, buttons: [], header: null },
  ];
}
